/**
 * Design-estimate state for one whiteboard (eval/TAKEOFF_PLAN.md "Design
 * estimate"): the catalog and pricing (live via `takeoff.changed` on
 * ws/boards/<id>/takeoff/), and server quantities + ROM budget, recomputed
 * whenever fixture elements change on the board (local or remote) or the
 * catalog does. Fixtures themselves are board elements; the server reads the
 * live scene, so every estimator sees the same totals and numbering.
 *
 * Returns a superset of what the shared catalog editors (ItemEditor,
 * AssemblyEditor, TotalsPanel, TakeoffExportMenu) use from a TakeoffSession.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiClient } from '../api';
import { toast } from '../components/basics';
import { applyTakeoffChange, type TakeoffSession, type TakeoffTarget } from './useTakeoff';
import {
  TakeoffConflict,
  takeoffApi,
  type PricingSettings,
  type QuantitiesDTO,
  type TakeoffAreaDTO,
  type TakeoffChange,
  type TakeoffItemDTO,
  type TakeoffStateDTO,
} from '../takeoff';
import { workbookApi } from '../workbook';

type Key = 'items' | 'assemblies' | 'areas';
const PATH: Record<Key, 'items' | 'assemblies' | 'areas'> = { items: 'items', assemblies: 'assemblies', areas: 'areas' };

export interface BoardEstimateOptions {
  boardId: string;
  api: ApiClient;
  enabled: boolean;
  /** ws(s)://host — defaults to the page origin (the board site proxies /ws/ to Django). */
  wsOrigin?: string;
  /** For tests: a WebSocket factory. */
  createSocket?: (url: string) => WebSocket;
}

export function useBoardEstimate(opts: BoardEstimateOptions) {
  const { boardId, api, enabled } = opts;
  const client = useMemo(() => takeoffApi(api, boardId, `/api/boards/${boardId}/takeoff/`), [api, boardId]);
  const workbook = useMemo(() => workbookApi(api, `/api/boards/${boardId}/takeoff/`), [api, boardId]);
  // Other socket frames (workbook.push_*) for panels that want them.
  const frameListeners = useRef(new Set<(type: string, data: Record<string, unknown>) => void>());
  const onFrame = useCallback((fn: (type: string, data: Record<string, unknown>) => void) => {
    frameListeners.current.add(fn);
    return () => {
      frameListeners.current.delete(fn);
    };
  }, []);
  const [state, setState] = useState<TakeoffStateDTO | null>(null);
  const [quantities, setQuantities] = useState<QuantitiesDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const alive = useRef(true);
  const qTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qSeq = useRef(0);

  const refreshQuantities = useCallback(
    (delay = 250) => {
      if (qTimer.current) clearTimeout(qTimer.current);
      qTimer.current = setTimeout(() => {
        qTimer.current = null;
        const n = ++qSeq.current;
        client
          .quantities()
          .then((q) => {
            if (alive.current && n === qSeq.current) {
              setQuantities(q);
              setError(null);
            }
          })
          .catch((e: Error & { status?: number }) => {
            if (alive.current && n === qSeq.current) setError(e.status === 503 ? 'Board sync is unavailable — totals will update when it is back.' : e.message);
          });
      }, delay);
    },
    [client],
  );

  const reload = useCallback(() => {
    client
      .state()
      .then((s) => {
        if (!alive.current) return;
        setState(s);
        setError(null);
      })
      .catch((e: Error) => alive.current && setError(e.message));
    refreshQuantities(0);
  }, [client, refreshQuantities]);

  useEffect(() => {
    alive.current = true;
    if (enabled) reload();
    return () => {
      alive.current = false;
      if (qTimer.current) clearTimeout(qTimer.current);
    };
  }, [enabled, reload]);

  // ---- live catalog / pricing ----------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const origin = opts.wsOrigin ?? (typeof location !== 'undefined' ? location.origin.replace(/^http/, 'ws') : '');
    const open = async () => {
      let token = api.tokens.access;
      if (!token) token = await api.refreshAccessToken();
      if (stopped || !token) return;
      const url = `${origin}/ws/boards/${encodeURIComponent(boardId)}/takeoff/?token=${encodeURIComponent(token)}`;
      ws = opts.createSocket ? opts.createSocket(url) : new WebSocket(url);
      ws.onmessage = (ev) => {
        let m: { type?: string; data?: unknown };
        try {
          m = JSON.parse(String(ev.data));
        } catch {
          return;
        }
        if (m.type === 'takeoff.ready') {
          attempt = 0;
          setLive(true);
          reload(); // anything missed while disconnected
          return;
        }
        if (m.type) for (const fn of frameListeners.current) fn(m.type, (m.data ?? {}) as Record<string, unknown>);
        if (m.type !== 'takeoff.changed') return;
        const change = m.data as TakeoffChange;
        const cur = stateRef.current;
        if (!cur) return;
        if (change.version && change.version > cur.version + 1) reload();
        else setState((s) => (s ? applyTakeoffChange(s, change) : s));
        refreshQuantities();
      };
      ws.onclose = (ev) => {
        setLive(false);
        if (stopped || ev.code === 4003) return;
        if (ev.code === 4001) void api.refreshAccessToken();
        const wait = Math.min(15_000, 500 * 2 ** attempt++);
        timer = setTimeout(() => void open(), wait);
      };
    };
    void open();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [enabled, boardId, api, reload, refreshQuantities, opts.wsOrigin, opts.createSocket]);

  // ---- catalog writes (held-version rule; server wins on conflict) ----------------------------
  const upsertLocal = (key: Key, row: { id: string }) =>
    setState((s) => {
      if (!s) return s;
      const list = (s[key] as unknown as { id: string }[]).filter((r) => r.id !== row.id);
      return { ...s, [key]: [...list, row] };
    });

  const create = useCallback(
    async <T extends { id: string }>(key: Key, data: Record<string, unknown>): Promise<T | null> => {
      try {
        const row = await client.create<T>(PATH[key], data);
        upsertLocal(key, row);
        refreshQuantities();
        return row;
      } catch (e) {
        toast(errorText(e), 'error');
        return null;
      }
    },
    [client, refreshQuantities],
  );

  const update = useCallback(
    async <T extends { id: string; version: number }>(key: Key, row: T, patch: Record<string, unknown>): Promise<T | null> => {
      try {
        const out = await client.update<T>(PATH[key], row.id, row.version, patch);
        upsertLocal(key, out);
        refreshQuantities();
        return out;
      } catch (e) {
        if (e instanceof TakeoffConflict) {
          upsertLocal(key, e.server as unknown as { id: string });
          toast(e.message, 'warning');
          return null;
        }
        toast(errorText(e), 'error');
        return null;
      }
    },
    [client, refreshQuantities],
  );

  const remove = useCallback(
    async (key: Key, id: string) => {
      try {
        await client.remove(PATH[key], id);
        setState((s) => (s ? { ...s, [key]: (s[key] as unknown as { id: string }[]).filter((r) => r.id !== id) } : s));
        refreshQuantities();
      } catch (e) {
        toast(errorText(e), 'error');
      }
    },
    [client, refreshQuantities],
  );

  const setPricing = useCallback(
    async (patch: Partial<PricingSettings>) => {
      try {
        const s = await client.setPricing(patch);
        setState(s);
        refreshQuantities(0);
      } catch (e) {
        toast(errorText(e), 'error');
      }
    },
    [client, refreshQuantities],
  );

  /** Frame settings (multiplier / scope / scale): create the frame's area row on first use. */
  const setFrame = useCallback(
    async (frameId: string, name: string, patch: Partial<TakeoffAreaDTO>) => {
      const row = stateRef.current?.areas.find((a) => a.frame_id === frameId);
      if (row) return update('areas', row, patch as Record<string, unknown>);
      return create<TakeoffAreaDTO>('areas', { name: name || 'Frame', frame_id: frameId, ...patch });
    },
    [create, update],
  );

  const items = state?.items ?? [];
  const assemblies = state?.assemblies ?? [];
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const assemblyById = useMemo(() => new Map(assemblies.map((a) => [a.id, a])), [assemblies]);

  const targetInfo = useCallback(
    (t: TakeoffTarget | null | undefined) => {
      if (!t) return null;
      if (t.kind === 'item') {
        const i = itemById.get(t.id);
        return i ? { label: `${i.code} ${i.description || i.name}`.trim(), short: i.code, color: i.color, symbol: i.symbol, uom: i.uom } : null;
      }
      const a = assemblyById.get(t.id);
      return a ? { label: a.name, short: a.code || abbreviation(a.name), color: a.color, symbol: a.symbol, uom: a.measure === 'count' ? 'EACH' : a.measure === 'linear' ? 'LF' : 'SF' } : null;
    },
    [itemById, assemblyById],
  );

  /** Element id → number ("LD01-3"), from the server's (placed_at, id) order. */
  const sequence = useMemo(() => new Map((quantities?.measurements ?? []).map((m) => [m.annotation_id, m.seq])), [quantities]);

  const uiStore = useMemo(() => ({ set: (p: { highlight?: string | null }) => p.highlight !== undefined && setHighlight(p.highlight) }), []);

  return {
    enabled,
    live,
    client,
    workbook,
    onFrame,
    state,
    quantities,
    budget: quantities?.budget ?? null,
    error,
    items,
    assemblies,
    itemById,
    assemblyById,
    targetInfo,
    sequence,
    reload,
    refreshQuantities,
    create,
    update,
    remove,
    setPricing,
    setFrame,
    ui: { highlight, tool: 'select' as const, target: null, runs: 1, unitTypeId: null, selectedMark: null },
    uiStore,
    sheetFor: () => null,
    setSheet: async () => null,
  };
}

export type BoardEstimate = ReturnType<typeof useBoardEstimate>;

/** The shared catalog editors take a TakeoffSession; a board estimate provides the parts they use. */
export function asTakeoffSession(e: BoardEstimate): TakeoffSession {
  return e as unknown as TakeoffSession;
}

export type { TakeoffItemDTO };

function abbreviation(name: string): string {
  return (name.replace(/-/g, ' ').split(/\s+/).filter(Boolean).slice(0, 4).map((w) => w[0]).join('') || 'H').toUpperCase();
}

function errorText(e: unknown): string {
  const err = e as { message?: string; body?: unknown };
  if (err.body && typeof err.body === 'object') {
    const first = Object.values(err.body as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return err.message ?? 'Request failed';
}
